import { useEffect, useMemo, useState } from 'react'
import { formatterPrice } from 'src/utils/formatterPrice'

import { Slider } from './Slider'
import { Accordion } from '../Accordion'

type Props = {
  max: number
  min: number
  onEnd: (value: number[]) => void
  defaultValue?: {
    to?: number
    from?: number
  }
}

type InputFieldProps = {
  label: string
  isEditing: boolean
  value: number
  formattedValue: string
  onFocus: () => void
  onBlur: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

const InputField = ({
  label,
  isEditing,
  value,
  formattedValue,
  onFocus,
  onBlur,
  onChange,
  onKeyDown,
}: InputFieldProps) => (
  <div className="flex flex-col w-[calc(50%-12px)]">
    <label className="text-sm font-normal text-restructure-primary">
      {label}
      <div className="mt-2">
        <input
          type={isEditing ? 'number' : 'text'}
          value={isEditing ? value : formattedValue}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={onKeyDown}
          className="w-full border border-gray py-5 px-4 text-base text-restructure-primary font-normal outline-none cursor-pointer focus-visible:border-restructure-background-blue focus-visible:border-[2px] !rounded-none hover:border-restructure-border-secondary-inverted"
        />
      </div>
    </label>
  </div>
)

const getValueFromPercent = (value: number, percent: number) =>
  Number((value * (percent / 100)).toFixed(2))

const getPercentFromValue = (current: number, max: number) =>
  max === 0 ? 0 : (current / max) * 100

export function RangePricing({
  max,
  min,
  defaultValue,
  onEnd,
}: Readonly<Props>) {
  const [percentRange, setPercentRange] = useState<[number, number]>([0, 100])
  const [oldInputValue, setOldInputValue] = useState<number[]>([min, max])
  const [inputsData, setInputsData] = useState({
    max: { value: max, isEditing: false },
    min: { value: min, isEditing: false },
  })

  useEffect(() => {
    if (!defaultValue?.to || !defaultValue?.from) {
      return
    }

    const toPercent = getPercentFromValue(defaultValue.to, max)
    const fromPercent = getPercentFromValue(defaultValue.from, max)

    setInputsData({
      max: { value: defaultValue.to, isEditing: false },
      min: { value: defaultValue.from, isEditing: false },
    })

    setOldInputValue([defaultValue.from, defaultValue.to])
    setPercentRange([fromPercent, toPercent])
  }, [defaultValue, max])

  const moneyValues = useMemo(
    () => [
      percentRange[0] === 0 ? min : getValueFromPercent(max, percentRange[0]),
      getValueFromPercent(max, percentRange[1]),
    ],
    [max, min, percentRange]
  )

  useEffect(() => {
    setPercentRange([
      getPercentFromValue(inputsData.min.value, max),
      getPercentFromValue(inputsData.max.value, max),
    ])
  }, [inputsData, max])

  const handleOnChange = () => {
    if (
      oldInputValue[0] === inputsData.min.value &&
      oldInputValue[1] === inputsData.max.value
    ) {
      return
    }

    setOldInputValue([inputsData.min.value, inputsData.max.value])
    onEnd([
      getValueFromPercent(max, percentRange[0]),
      getValueFromPercent(max, percentRange[1]),
    ])
  }

  const handleSliderChangeEnd = (value: [number, number]) => {
    setInputsData({
      min: { ...inputsData.min, value: getValueFromPercent(max, value[0]) },
      max: { ...inputsData.max, value: getValueFromPercent(max, value[1]) },
    })
    onEnd([
      getValueFromPercent(max, value[0]),
      getValueFromPercent(max, value[1]),
    ])
  }

  function updateInputsData(
    key: 'min' | 'max',
    newData: Partial<{ value: number; isEditing: boolean }>
  ) {
    setOldInputValue([inputsData.min.value, inputsData.max.value])
    setInputsData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...newData,
      },
    }))
  }

  return (
    <Accordion
      title="Faixa de preço"
      index={2}
      subTitle={`De ${formatterPrice(moneyValues[0])} até ${formatterPrice(
        moneyValues[1]
      )}`}
    >
      <div className="mt-5 flex flex-col gap-8">
        <Slider
          value={percentRange}
          onAfterChange={handleSliderChangeEnd}
          onChange={setPercentRange}
          ariaLabel={percentRange.map(String)}
        />

        <div className="flex justify-between">
          <InputField
            label="Preço mínimo"
            isEditing={inputsData.min.isEditing}
            value={inputsData.min.value}
            formattedValue={formatterPrice(moneyValues[0])}
            onFocus={() => updateInputsData('min', { isEditing: true })}
            onBlur={() => {
              updateInputsData('min', { isEditing: false })
              handleOnChange()
            }}
            onChange={(e) =>
              updateInputsData('min', {
                isEditing: true,
                value: Number(e.target.value),
              })
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleOnChange()
            }}
          />

          <InputField
            label="Preço máximo"
            isEditing={inputsData.max.isEditing}
            value={inputsData.max.value}
            formattedValue={formatterPrice(moneyValues[1])}
            onFocus={() => updateInputsData('max', { isEditing: true })}
            onBlur={() => {
              updateInputsData('max', { isEditing: false })
              handleOnChange()
            }}
            onChange={(e) =>
              updateInputsData('max', { value: Number(e.target.value) })
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleOnChange()
            }}
          />
        </div>
      </div>
    </Accordion>
  )
}
